/**
 * planning-drag — helper partagé pour interpréter les fins de drag de la grille.
 *
 * La grille `PlanningGrilleUnifiee` expose deux natures de drag :
 *   - un POSTE glissé depuis la barre « Postes à glisser » (data `{ poste }`) ;
 *   - un CRÉNEAU déjà posé qu'on déplace (data `{ type: 'creneau', payload }`).
 *
 * Les zones droppables portent `{ dayIdx, hour }`. Ce helper centralise le calcul
 * pour que TOUTES les briques réagissent pareil (notamment le re-déplacement d'un
 * créneau, qui n'était géré que par le service technique).
 */
import type { DragEndEvent } from '@dnd-kit/core';
import type { CreneauGrille } from '@/components/planning/PlanningGrilleUnifiee';

export type PlanningDropResult<P> =
  | { kind: 'poste'; poste: P; jour: Date }
  | { kind: 'move'; creneau: CreneauGrille; nouveauDebut: Date; nouvelleFin: Date }
  | null;

/**
 * Interprète un `onDragEnd`.
 * @param event   L'évènement dnd-kit.
 * @param weekDays Les 7 dates de la semaine affichée (index = dayIdx).
 */
export function interpretPlanningDrop<P>(event: DragEndEvent, weekDays: Date[]): PlanningDropResult<P> {
  const { active, over } = event;
  if (!over) return null;
  const overData = over.data.current as { dayIdx?: number; hour?: number } | undefined;
  if (!overData || overData.dayIdx == null || overData.hour == null) return null;
  const base = weekDays[overData.dayIdx];
  if (!base) return null;

  const activeData = active.data.current as
    | { poste?: P; type?: string; payload?: CreneauGrille }
    | undefined;

  // Cas 1 : déplacement d'un créneau existant → on conserve la durée.
  if (activeData?.type === 'creneau' && activeData.payload) {
    const c = activeData.payload;
    const dureeMs = new Date(c.dateFin).getTime() - new Date(c.dateDebut).getTime();
    const nouveauDebut = new Date(base);
    nouveauDebut.setHours(overData.hour, 0, 0, 0);
    const nouvelleFin = new Date(nouveauDebut.getTime() + dureeMs);
    return { kind: 'move', creneau: c, nouveauDebut, nouvelleFin };
  }

  // Cas 2 : dépose d'un poste (chip) → création d'un créneau.
  if (activeData?.poste != null) {
    const jour = new Date(base);
    jour.setHours(overData.hour, 0, 0, 0);
    return { kind: 'poste', poste: activeData.poste, jour };
  }

  return null;
}
